import {
    AfterViewInit,
    Component,
    EventEmitter,
    inject,
    OnInit,
} from '@angular/core';
import { IDateRangePickerDashboardItem } from 'src/app/components/date-picker/date-picker.component';
import { TableDataItem } from 'src/app/components/table-dashboard-item/table-dashboard-item.component';
import {
    FLOW_POPULATIONS,
    FLOW_USER_TYPES,
    FLOW_VALUE_MODE_OPTIONS,
} from 'src/app/dashboards/mopa-resource-flow/resource-flow.models';
import { CommonLegendService } from 'src/app/services/common-legend.service';
import { IDashboardItemDataLoaderLink } from 'src/app/services/dashboard-item/IDashboardItemDataLoaderLink';
import { DashboardService } from 'src/app/services/dashboard/dashboard.service';
import { IDataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { DataLoaderFactories } from 'src/app/services/data-loader/data-loader.service';
import { currentDataProject } from 'src/app/services/data-project/models/data-project.model';
import { FlowDashboardStateService } from 'src/app/services/flow-dashboard/flow-dashboard-state.service';
import {
    FlowAxis,
    getFlowAxis,
} from 'src/app/services/flow-dashboard/flow-dashboard.models';
import {
    IQueryContext,
    QueryContextSettingID,
} from 'src/app/services/query-context/models/IQueryContext';
import { QueryContextService } from 'src/app/services/query-context/query-context.service';
import {
    DatasourceColumnId,
    datasourceColumnOrderByQuery,
} from 'src/app/utils/datasource-sql-map/datasource';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { DatasourceColumnOldDataLoaderFactoryOptions } from '../common/data-loaders/datasource-column-old';
import { SegmentDatasourceColumnDataLoaderFactoryOptions } from '../common/data-loaders/segments';
import { DashboardItemFactories } from '../common/items/common-dashboard-items';
import { FilterCategoryDashboardItemFactoryOptions } from '../common/items/filter/category-filter-dashboard-item/filter-category';
import { createFlowBreakdownTableDashboardItem } from '../common/items/flow-breakdown-table';
import { ModifyTimeDashboardItemFactoryOptions } from '../common/items/modify-time';
import { ICheckboxSettingDashboardItem } from '../common/items/setting/checkbox/checkbox.component';
import { ISingleSelectSettingDashboardItem } from '../common/items/setting/single-select/single-select.component';
import { IToggleSettingDashboardItem } from '../common/items/setting/toggle/toggle.component';
import { StandardDashboardWorker } from '../common/standard-dashboard';
import { BiVizDashboardResourcesSqlMap } from '../mopa-resources-detailed/sql-maps/biviz-dashboard-resources-datasource-sql-map';
import { ResourceFlowByDateDataLoaderFactory } from './data-loaders/resource-flow-by-date';
import { ResourceFlowByDayNoDataLoaderFactory } from './data-loaders/resource-flow-by-day-no';
import { ResourceFlowKpiDataLoaderFactory } from './data-loaders/resource-flow-kpi';
import { MopaResourceFlowChartDocs } from './mopa-resource-flow.chart-documentation';

interface ResourceFlowFilterSection {
    title: string;
    accent: 'chart' | 'activity' | 'audience' | 'context';
    columnIds: DatasourceColumnId[];
    includesMetricSettings?: boolean;
}

/** Resource Flow uses one by-date loader for charts and breakdowns; KPIs use a range-wide loader. */
@Component({
    selector: 'app-mopa-resource-flow',
    templateUrl: './mopa-resource-flow.component.html',
    styleUrls: ['./mopa-resource-flow.component.scss'],
    providers: [
        DashboardService,
        QueryContextService,
        CommonLegendService,
        FlowDashboardStateService,
    ],
})
export class MopaResourceFlowComponent
    extends StandardDashboardWorker
    implements OnInit, AfterViewInit
{
    override DASHBOARD_NAME = 'Resource Flow';
    override DASHBOARD_ID = 'mopa-resource-flow';
    override dashboardDocumentation: string =
        MopaResourceFlowChartDocs.dashboard;

    private gcpProjectId: GcpProjectId = currentDataProject().gcpProjectId;

    /////////////////////////////////////////////////////////////////
    ////
    ////    DATALOADERS
    /////////////////////////////////////////////////////////////////
    override filterDataLoaders = {
        'mopa.resource-flow.filter.segment': DataLoaderFactories[
            'common.filter.segment'
        ](
            new SegmentDatasourceColumnDataLoaderFactoryOptions(
                'mopa.resource-flow.filter_segments',
                this.gcpProjectId,
                this.queryCache
            )
        ).create(),
        'mopa.resource-flow.filter.resources_source': DataLoaderFactories[
            'common.filter.datasource_column_old'
        ](
            new DatasourceColumnOldDataLoaderFactoryOptions({
                createdInstanceId: 'mopa.resource-flow.filter.resources_source',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'RESOURCES_SOURCE',
                datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
                filterContext: true,
            })
        ).create(),
        'mopa.resource-flow.filter.resource_source_category':
            DataLoaderFactories['common.filter.datasource_column_old'](
                new DatasourceColumnOldDataLoaderFactoryOptions({
                    createdInstanceId:
                        'mopa.resource-flow.filter.resource_source_category',
                    gcpProjectId: this.gcpProjectId,
                    queryCacheService: this.queryCache,
                    datasourceColumnId: 'RESOURCE_SOURCE_CATEGORY',
                    datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
                    filterContext: true,
                })
            ).create(),
        'mopa.resource-flow.filter.resource_payment_type': DataLoaderFactories[
            'common.filter.datasource_column_old'
        ](
            new DatasourceColumnOldDataLoaderFactoryOptions({
                createdInstanceId:
                    'mopa.resource-flow.filter.resource_payment_type',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'RESOURCE_PAYMENT_TYPE',
                datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
                filterContext: true,
                orderByQuery: datasourceColumnOrderByQuery(
                    'RESOURCE_PAYMENT_TYPE'
                ),
            })
        ).create(),
        'mopa.resource-flow.filter.firebase_app_store': DataLoaderFactories[
            'common.filter.datasource_column_old'
        ](
            new DatasourceColumnOldDataLoaderFactoryOptions({
                createdInstanceId:
                    'mopa.resource-flow.filter.firebase_app_store',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIREBASE_APP_STORE',
                datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
            })
        ).create(),
        'mopa.resource-flow.filter.firebase_country_tier': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId:
                'mopa.resource-flow.filter.firebase_country_tier',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FIREBASE_COUNTRY_TIER',
            datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
        }).create(),
        'mopa.resource-flow.filter.etl_revenue_badge': DataLoaderFactories[
            'common.filter.datasource_column_old'
        ](
            new DatasourceColumnOldDataLoaderFactoryOptions({
                createdInstanceId:
                    'mopa.resource-flow.filter.etl_revenue_badge',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'ETL_REVENUE_BADGE',
                datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
                orderByQuery: datasourceColumnOrderByQuery('ETL_REVENUE_BADGE'),
            })
        ).create(),
        'mopa.resource-flow.filter.resource_source_name': DataLoaderFactories[
            'common.filter.datasource_column_old'
        ](
            new DatasourceColumnOldDataLoaderFactoryOptions({
                createdInstanceId:
                    'mopa.resource-flow.filter.resource_source_name',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'RESOURCE_SOURCE_NAME',
                datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
                filterContext: true,
            })
        ).create(),
        'mopa.resource-flow.filter.activity_app_version': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.resource-flow.filter.activity_app_version',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'ACTIVITY_APP_VERSION',
            datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
            orderByQuery: datasourceColumnOrderByQuery('ACTIVITY_APP_VERSION'),
        }).create(),
        'mopa.resource-flow.filter.firebase_country': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.resource-flow.filter.firebase_country',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FIREBASE_COUNTRY',
            datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
            filterContext: ['FIREBASE_COUNTRY_TIER'],
        }).create(),
        'mopa.resource-flow.filter.attribution_traffic_source':
            DataLoaderFactories['common.filter.datasource_column_old'](
                new DatasourceColumnOldDataLoaderFactoryOptions({
                    createdInstanceId:
                        'mopa.resource-flow.filter.attribution_traffic_source',
                    gcpProjectId: this.gcpProjectId,
                    queryCacheService: this.queryCache,
                    datasourceColumnId: 'ATTRIBUTION_TRAFFIC_SOURCE',
                    datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
                })
            ).create(),
        'mopa.resource-flow.filter.etl_payer': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.resource-flow.filter.etl_payer',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'ETL_PAYER',
            datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
        }).create(),
        'mopa.resource-flow.filter.change_type': DataLoaderFactories[
            'common.filter.datasource_column_old'
        ](
            new DatasourceColumnOldDataLoaderFactoryOptions({
                createdInstanceId: 'mopa.resource-flow.filter.change_type',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'CHANGE_TYPE',
                datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
                filterContext: true,
            })
        ).create(),
        'mopa.resource-flow.filter.install_day_no': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.resource-flow.filter.install_day_no',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'INSTALL_DAY_NO',
            datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
            filterContext: true,
            orderByQuery: datasourceColumnOrderByQuery('INSTALL_DAY_NO'),
        }).create(),
        'mopa.resource-flow.filter.activity_app_version_group_minor':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.resource-flow.filter.activity_app_version_group_minor',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'ACTIVITY_APP_VERSION_GROUP_MINOR',
                datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
                orderByQuery: datasourceColumnOrderByQuery(
                    'ACTIVITY_APP_VERSION_GROUP_MINOR'
                ),
            }).create(),
        'mopa.resource-flow.filter.firebase_country_european_union':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.resource-flow.filter.firebase_country_european_union',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIREBASE_COUNTRY_EUROPEAN_UNION',
                datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
            }).create(),
        'mopa.resource-flow.filter.firebase_install_version_group_minor':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.resource-flow.filter.firebase_install_version_group_minor',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIREBASE_INSTALL_VERSION_GROUP_MINOR',
                datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
                filterContext: true,
                orderByQuery: datasourceColumnOrderByQuery(
                    'FIREBASE_INSTALL_VERSION_GROUP_MINOR'
                ),
            }).create(),
        'mopa.resource-flow.filter.firebase_install_version':
            DataLoaderFactories['common.filter.datasource_column_old'](
                new DatasourceColumnOldDataLoaderFactoryOptions({
                    createdInstanceId:
                        'mopa.resource-flow.filter.firebase_install_version',
                    gcpProjectId: this.gcpProjectId,
                    queryCacheService: this.queryCache,
                    datasourceColumnId: 'FIREBASE_INSTALL_VERSION',
                    datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
                    filterContext: true,
                    orderByQuery: datasourceColumnOrderByQuery(
                        'FIREBASE_INSTALL_VERSION'
                    ),
                })
            ).create(),
        'mopa.resource-flow.filter.attribution_network_name':
            DataLoaderFactories['common.filter.datasource_column_old'](
                new DatasourceColumnOldDataLoaderFactoryOptions({
                    createdInstanceId:
                        'mopa.resource-flow.filter.attribution_network_name',
                    gcpProjectId: this.gcpProjectId,
                    queryCacheService: this.queryCache,
                    datasourceColumnId: 'ATTRIBUTION_NETWORK_NAME',
                    datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
                })
            ).create(),
        'mopa.resource-flow.filter.attribution_campaign_name':
            DataLoaderFactories['common.filter.datasource_column_old'](
                new DatasourceColumnOldDataLoaderFactoryOptions({
                    createdInstanceId:
                        'mopa.resource-flow.filter.attribution_campaign_name',
                    gcpProjectId: this.gcpProjectId,
                    queryCacheService: this.queryCache,
                    datasourceColumnId: 'ATTRIBUTION_CAMPAIGN_NAME',
                    datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
                    filterContext: true,
                })
            ).create(),
    };

    override chartDataLoaders = {
        'mopa.resource-flow.modify_time_resources_datasource':
            DataLoaderFactories['common.modify_time']({
                createdInstanceId:
                    'mopa.resource-flow.modify_time_resources_datasource',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasetId: BiVizDashboardResourcesSqlMap.sqlMap.dataset,
                tableId: BiVizDashboardResourcesSqlMap.sqlMap.tableId,
            }).create(),
        'mopa.resource-flow.by_date': new ResourceFlowByDateDataLoaderFactory({
            createdInstanceId: 'mopa.resource-flow.by_date',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
        }).create(),
        'mopa.resource-flow.kpi': new ResourceFlowKpiDataLoaderFactory({
            createdInstanceId: 'mopa.resource-flow.kpi',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
        }).create(),
        'mopa.resource-flow.by_day_no':
            new ResourceFlowByDayNoDataLoaderFactory({
                createdInstanceId: 'mopa.resource-flow.by_day_no',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
            }).create(),
    };

    /////////////////////////////////////////////////////////////////
    ////
    ////    [DASHBOARD ITEM <-> DATALOADER] LINKS
    ////
    /////////////////////////////////////////////////////////////////

    // SETTINGS: re-query on Apply.
    currencySetting: ISingleSelectSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.RESOURCE_FLOW_CURRENCY,
            viewtype: 'singleSelect',
            displayName: 'Currency',
            value: 'gems',
            selectOptions: [...BiVizDashboardResourcesSqlMap.ALL_RESOURCES],
        },
    };
    xAxisSetting: IToggleSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.FLOW_X_AXIS,
            viewtype: 'toggle',
            displayName: 'X axis',
            value: 'date',
            selectOptions: ['date', 'install_day', 'active_day'],
        },
    };
    accumulationSetting: IToggleSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.FLOW_ACCUMULATION,
            viewtype: 'toggle',
            displayName: 'Accumulation',
            value: 'daily',
            selectOptions: ['daily', 'running'],
        },
    };
    valueModeSetting: ISingleSelectSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.FLOW_VALUE_MODE,
            viewtype: 'singleSelect',
            displayName: 'Value mode',
            description: `Statistic calculated for each side of the flow (inflow ▲ and outflow ▼) separately: TOTAL, AVERAGE, MEDIAN / PERCENTILE, FLOW RECORDS, PLAYERS.

P50 / P75 / P90 / P99 work for MEDIAN / PERCENTILE only.

Definitions and formulas of every mode are in the chart documentation.`,
            value: 'total',
            selectOptions: [...FLOW_VALUE_MODE_OPTIONS],
        },
    };
    userTypeSetting: IToggleSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.FLOW_USER_TYPE,
            viewtype: 'toggle',
            displayName: 'User type',
            description: `Filters flow and denominators to daily HOPA or META users. HOPA spends energy >= 1; META opens the decoration menu. Groups overlap and do not add up to ALL. Measured from 2026-06-11.`,
            value: 'all',
            selectOptions: [...FLOW_USER_TYPES],
        },
    };
    populationSetting: IToggleSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.FLOW_POPULATION,
            viewtype: 'toggle',
            displayName: 'Population',
            description: `Denominator of the selected statistic: DEU (only players with a positive flow of this currency on that side) or DAU (every active player).

Works for AVERAGE and MEDIAN / PERCENTILE only.`,
            value: 'deu',
            selectOptions: [...FLOW_POPULATIONS],
        },
    };
    settingShowP50: ICheckboxSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.SHOW_PERCENTILE_50,
            viewtype: 'checkbox',
            displayName: 'P50',
            value: true,
        },
    };
    settingShowP75: ICheckboxSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.SHOW_PERCENTILE_75,
            viewtype: 'checkbox',
            displayName: 'P75',
            value: false,
        },
    };
    settingShowP90: ICheckboxSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.SHOW_PERCENTILE_90,
            viewtype: 'checkbox',
            displayName: 'P90',
            value: false,
        },
    };
    settingShowP99: ICheckboxSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.SHOW_PERCENTILE_99,
            viewtype: 'checkbox',
            displayName: 'P99',
            value: false,
        },
    };

    // CHARTS
    flowState = inject(FlowDashboardStateService);

    flowChart: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resource-flow.flow',
        dashboardItemFactory: DashboardItemFactories['common.chart.flow_chart'](
            {
                createdInstanceId: 'mopa.resource-flow.flow',
                dashboardName: this.DASHBOARD_NAME,
                title: 'Inflow ▲ / Outflow ▼',
                chartDocumentation: MopaResourceFlowChartDocs.dashboard,
                variant: 'flow',
                onChartInstance: (e) => this.flowState.ensureWired(e, 'flow'),
            }
        ),
        dataLoaderEmitter: this.flowState.flowViewModel$,
    };
    netChart: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resource-flow.net',
        dashboardItemFactory: DashboardItemFactories['common.chart.flow_chart'](
            {
                createdInstanceId: 'mopa.resource-flow.net',
                dashboardName: this.DASHBOARD_NAME,
                title: 'Net balance',
                variant: 'net',
                onChartInstance: (e) => this.flowState.ensureWired(e, 'net'),
            }
        ),
        dataLoaderEmitter: this.flowState.netViewModel$,
    };

    kpiTiles: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resource-flow.kpi_tiles',
        dashboardItemFactory: DashboardItemFactories['common.kpi_tiles']({
            createdInstanceId: 'mopa.resource-flow.kpi_tiles',
            composeData: (tiles) => {
                const documentation =
                    MopaResourceFlowChartDocs.affected_players_tile;
                return tiles.map((tile) =>
                    tile.label === 'Affected players'
                        ? { ...tile, documentation }
                        : tile
                );
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resource-flow.kpi'].emitter,
    };

    modify_time_resources_datasource: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resource-flow.modify_time_resources_datasource',
        dashboardItemFactory: DashboardItemFactories['common.modify_time'](
            new ModifyTimeDashboardItemFactoryOptions(
                'mopa.resource-flow.modify_time_resources_datasource',
                'value'
            )
        ),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.resource-flow.modify_time_resources_datasource'
            ].emitter,
    };

    // Breakdown tables mirror the legend.
    readonly breakdowns = [
        {
            direction: 'in' as const,
            ariaLabel: 'Inflow legend controls',
            dashboardItem: createFlowBreakdownTableDashboardItem({
                dashboardName: this.DASHBOARD_NAME,
                instanceId: 'mopa.resource-flow.inflow-breakdown',
                title: '▲ Inflow',
            }),
            dataLoaderEmitter: this.flowState.inflowRows$,
        },
        {
            direction: 'out' as const,
            ariaLabel: 'Outflow legend controls',
            dashboardItem: createFlowBreakdownTableDashboardItem({
                dashboardName: this.DASHBOARD_NAME,
                instanceId: 'mopa.resource-flow.outflow-breakdown',
                title: '▼ Outflow',
            }),
            dataLoaderEmitter: this.flowState.outflowRows$,
        },
    ];

    // FILTERS: sidebar order; categories are splitable by default.

    filterDashboardItemLinks: Partial<
        Record<DatasourceColumnId, IDashboardItemDataLoaderLink>
    > = {
        CHANGE_TYPE: {
            id: 'mopa.resource-flow.filter.change_type',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resource-flow.filter.change_type',
                    'CHANGE_TYPE'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.resource-flow.filter.change_type']
                    .emitter,
        },
        RESOURCE_PAYMENT_TYPE: {
            id: 'mopa.resource-flow.filter.resource_payment_type',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId:
                    'mopa.resource-flow.filter.resource_payment_type',
                datasourceColumnId: 'RESOURCE_PAYMENT_TYPE',
                bucketable: true,
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.resource-flow.filter.resource_payment_type'
                ].emitter,
        },
        RESOURCE_SOURCE_CATEGORY: {
            id: 'mopa.resource-flow.filter.resource_source_category',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId:
                    'mopa.resource-flow.filter.resource_source_category',
                datasourceColumnId: 'RESOURCE_SOURCE_CATEGORY',
                bucketable: true,
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.resource-flow.filter.resource_source_category'
                ].emitter,
        },
        RESOURCE_SOURCE_NAME: {
            id: 'mopa.resource-flow.filter.resource_source_name',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resource-flow.filter.resource_source_name',
                    'RESOURCE_SOURCE_NAME'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.resource-flow.filter.resource_source_name'
                ].emitter,
        },
        RESOURCES_SOURCE: {
            id: 'mopa.resource-flow.filter.resources_source',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resource-flow.filter.resources_source',
                    'RESOURCES_SOURCE'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.resource-flow.filter.resources_source'
                ].emitter,
        },
        ACTIVITY_DATE: {
            id: 'mopa.resource-flow.filter.activity_date',
            dashboardItemFactory: DashboardItemFactories['filter.daterange']({
                createdInstanceId: 'mopa.resource-flow.filter.activity_date',
                datasourceColumnId: 'ACTIVITY_DATE',
                splitable: false,
            }),
            dataLoaderEmitter: new EventEmitter<IDataLoaderResult>(),
        },
        PLAYER_ID: {
            id: 'mopa.resource-flow.filter.player_id',
            dashboardItemFactory: DashboardItemFactories['filter.string_input'](
                {
                    createdInstanceId: 'mopa.resource-flow.filter.player_id',
                    datasourceColumnId: 'PLAYER_ID',
                }
            ),
            dataLoaderEmitter: new EventEmitter<IDataLoaderResult>(),
        },
        INSTALL_DAY_NO: {
            id: 'mopa.resource-flow.filter.install_day_no',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId: 'mopa.resource-flow.filter.install_day_no',
                datasourceColumnId: 'INSTALL_DAY_NO',
                splitable: false,
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.resource-flow.filter.install_day_no'
                ].emitter,
        },
        ACTIVITY_APP_VERSION_GROUP_MINOR: {
            id: 'mopa.resource-flow.filter.activity_app_version_group_minor',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resource-flow.filter.activity_app_version_group_minor',
                    'ACTIVITY_APP_VERSION_GROUP_MINOR'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.resource-flow.filter.activity_app_version_group_minor'
                ].emitter,
        },
        ACTIVITY_APP_VERSION: {
            id: 'mopa.resource-flow.filter.activity_app_version',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resource-flow.filter.activity_app_version',
                    'ACTIVITY_APP_VERSION'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.resource-flow.filter.activity_app_version'
                ].emitter,
        },
        SEGMENT: {
            id: 'mopa.resource-flow.filter.segment',
            dashboardItemFactory: DashboardItemFactories['filter.segments']({
                createdInstanceId: 'mopa.resource-flow.filter.segment',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.resource-flow.filter.segment']
                    .emitter,
        },
        FIREBASE_FIRST_OPEN_DATE: {
            id: 'mopa.resource-flow.filter.firebase_first_open_date',
            dashboardItemFactory: DashboardItemFactories['filter.daterange']({
                createdInstanceId:
                    'mopa.resource-flow.filter.firebase_first_open_date',
                datasourceColumnId: 'FIREBASE_FIRST_OPEN_DATE',
                splitable: false,
            }),
            dataLoaderEmitter: new EventEmitter<IDataLoaderResult>(),
        },
        FIREBASE_APP_STORE: {
            id: 'mopa.resource-flow.filter.firebase_app_store',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resource-flow.filter.firebase_app_store',
                    'FIREBASE_APP_STORE'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.resource-flow.filter.firebase_app_store'
                ].emitter,
        },
        FIREBASE_COUNTRY_EUROPEAN_UNION: {
            id: 'mopa.resource-flow.filter.firebase_country_european_union',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resource-flow.filter.firebase_country_european_union',
                    'FIREBASE_COUNTRY_EUROPEAN_UNION'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.resource-flow.filter.firebase_country_european_union'
                ].emitter,
        },
        FIREBASE_COUNTRY_TIER: {
            id: 'mopa.resource-flow.filter.firebase_country_tier',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resource-flow.filter.firebase_country_tier',
                    'FIREBASE_COUNTRY_TIER'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.resource-flow.filter.firebase_country_tier'
                ].emitter,
        },
        FIREBASE_COUNTRY: {
            id: 'mopa.resource-flow.filter.firebase_country',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resource-flow.filter.firebase_country',
                    'FIREBASE_COUNTRY'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.resource-flow.filter.firebase_country'
                ].emitter,
        },
        ATTRIBUTION_TRAFFIC_SOURCE: {
            id: 'mopa.resource-flow.filter.attribution_traffic_source',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resource-flow.filter.attribution_traffic_source',
                    'ATTRIBUTION_TRAFFIC_SOURCE'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.resource-flow.filter.attribution_traffic_source'
                ].emitter,
        },
        FIREBASE_INSTALL_VERSION_GROUP_MINOR: {
            id: 'mopa.resource-flow.filter.firebase_install_version_group_minor',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resource-flow.filter.firebase_install_version_group_minor',
                    'FIREBASE_INSTALL_VERSION_GROUP_MINOR'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.resource-flow.filter.firebase_install_version_group_minor'
                ].emitter,
        },
        FIREBASE_INSTALL_VERSION: {
            id: 'mopa.resource-flow.filter.firebase_install_version',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resource-flow.filter.firebase_install_version',
                    'FIREBASE_INSTALL_VERSION'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.resource-flow.filter.firebase_install_version'
                ].emitter,
        },
        ETL_PAYER: {
            id: 'mopa.resource-flow.filter.etl_payer',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resource-flow.filter.etl_payer',
                    'ETL_PAYER'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.resource-flow.filter.etl_payer']
                    .emitter,
        },
        ETL_REVENUE_BADGE: {
            id: 'mopa.resource-flow.filter.etl_revenue_badge',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resource-flow.filter.etl_revenue_badge',
                    'ETL_REVENUE_BADGE'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.resource-flow.filter.etl_revenue_badge'
                ].emitter,
        },
        ATTRIBUTION_NETWORK_NAME: {
            id: 'mopa.resource-flow.filter.attribution_network_name',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resource-flow.filter.attribution_network_name',
                    'ATTRIBUTION_NETWORK_NAME'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.resource-flow.filter.attribution_network_name'
                ].emitter,
        },
        ATTRIBUTION_CAMPAIGN_NAME: {
            id: 'mopa.resource-flow.filter.attribution_campaign_name',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resource-flow.filter.attribution_campaign_name',
                    'ATTRIBUTION_CAMPAIGN_NAME'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.resource-flow.filter.attribution_campaign_name'
                ].emitter,
        },
    };

    readonly filterDashboardItemSections: ResourceFlowFilterSection[] = [
        {
            title: 'Chart specific',
            accent: 'chart',
            includesMetricSettings: true,
            columnIds: [
                'CHANGE_TYPE',
                'RESOURCE_PAYMENT_TYPE',
                'RESOURCE_SOURCE_CATEGORY',
                'RESOURCE_SOURCE_NAME',
                'RESOURCES_SOURCE',
            ],
        },
        {
            title: 'Activity',
            accent: 'activity',
            columnIds: [
                'ACTIVITY_DATE',
                'INSTALL_DAY_NO',
                'ACTIVITY_APP_VERSION_GROUP_MINOR',
                'ACTIVITY_APP_VERSION',
            ],
        },
        {
            title: 'Audience',
            accent: 'audience',
            columnIds: ['SEGMENT', 'PLAYER_ID'],
        },
        {
            title: 'Player context',
            accent: 'context',
            columnIds: [
                'FIREBASE_FIRST_OPEN_DATE',
                'FIREBASE_APP_STORE',
                'FIREBASE_COUNTRY_EUROPEAN_UNION',
                'FIREBASE_COUNTRY_TIER',
                'FIREBASE_COUNTRY',
                'ATTRIBUTION_TRAFFIC_SOURCE',
                'ATTRIBUTION_NETWORK_NAME',
                'ATTRIBUTION_CAMPAIGN_NAME',
                'FIREBASE_INSTALL_VERSION_GROUP_MINOR',
                'FIREBASE_INSTALL_VERSION',
                'ETL_PAYER',
                'ETL_REVENUE_BADGE',
            ],
        },
    ];

    override async ngOnInit() {
        super.ngOnInit();
        // Attach before child view initialization.
        this.attachAxis('date');
    }

    /** Point the Flow composition at one axis' loader; `attach` drops the previous subscription. */
    private attachAxis(axis: FlowAxis) {
        const isByDayNo = axis !== 'date';
        this.flowState.attach(
            this.chartDataLoaders[
                isByDayNo
                    ? 'mopa.resource-flow.by_day_no'
                    : 'mopa.resource-flow.by_date'
            ].emitter,
            {
                instanceId: 'mopa.resource-flow',
                colorMode: 'flow',
                dateKey: isByDayNo ? 'day_no' : 'date',
                xAxisSortFn: isByDayNo
                    ? (a, b) => Number(a) - Number(b)
                    : undefined,
            }
        );
    }

    accumulationDisabled = true;
    populationDisabled = true;
    percentilesDisabled = true;

    private syncSettings(context: IQueryContext) {
        const valueMode =
            context.settings[QueryContextSettingID.FLOW_VALUE_MODE] ?? 'total';
        this.accumulationDisabled = getFlowAxis(context) === 'date';
        this.populationDisabled =
            valueMode !== 'average' && valueMode !== 'percentile';
        this.percentilesDisabled = valueMode !== 'percentile';
    }

    override async changePendingQueryContextHandler(context: IQueryContext) {
        this.syncSettings(context);
        await super.changePendingQueryContextHandler(context);
    }

    override async changeAppliedQueryContextHandler(context: IQueryContext) {
        this.syncSettings(context);
        // Re-point before the loaders run, so the charts never consume the other axis' emission.
        this.attachAxis(getFlowAxis(context));
        await super.changeAppliedQueryContextHandler(context);
    }

    ngAfterViewInit() {
        this.standardDashboardInitialize();
    }

    /** Toggle a series from its breakdown row. */
    toggleSeriesFromRow(row: TableDataItem) {
        const seriesName = (row.meta as { seriesName?: string } | undefined)
            ?.seriesName;
        if (seriesName) {
            this.flowState.toggleSeries(seriesName);
        }
    }

    /** Show or hide one breakdown side. */
    setBreakdownSideVisibility(dir: 'in' | 'out', visible: boolean) {
        this.flowState.setSideSeriesVisibility(dir, visible);
    }

    override getInitialContext(): IQueryContext {
        const initialContext = QueryContextService.INITIAL_CONTEXT;
        const activityDateIC: IDateRangePickerDashboardItem['datasourceColumn'] =
            {
                id: 'ACTIVITY_DATE',
                state: {
                    value: { preset: 'last 90 days', splitAggregate: 'day' },
                    isFilterApplied: true,
                    isSplitApplied: false,
                },
            };
        const firstOpenDateIC: IDateRangePickerDashboardItem['datasourceColumn'] =
            {
                id: 'FIREBASE_FIRST_OPEN_DATE',
                state: {
                    value: { preset: 'last 90 days', splitAggregate: 'day' },
                    isFilterApplied: true,
                    isSplitApplied: false,
                },
            };
        initialContext.datasourceColumns = [activityDateIC, firstOpenDateIC];
        initialContext.settings = {
            ...initialContext.settings,
            [QueryContextSettingID.RESOURCE_FLOW_CURRENCY]: 'gems',
            [QueryContextSettingID.FLOW_X_AXIS]: 'date',
            [QueryContextSettingID.FLOW_ACCUMULATION]: 'daily',
            [QueryContextSettingID.FLOW_VALUE_MODE]: 'total',
            [QueryContextSettingID.FLOW_POPULATION]: 'deu',
            [QueryContextSettingID.FLOW_USER_TYPE]: 'all',
            [QueryContextSettingID.SHOW_PERCENTILE_50]: true,
            [QueryContextSettingID.SHOW_PERCENTILE_75]: false,
            [QueryContextSettingID.SHOW_PERCENTILE_90]: false,
            [QueryContextSettingID.SHOW_PERCENTILE_99]: false,
        };
        return initialContext;
    }
}
